import type { SeasonPreference } from '@features/seasonal-theme/types';

import { isSeasonId } from '@features/seasonal-theme/utils/resolve-season';

const STORAGE_KEY = 'season-preference';
const URL_PARAM = 'season';

const listeners = new Set<() => void>();

export const isSeasonPreference = (value: string | null): value is SeasonPreference =>
  value === 'auto' || value === 'off' || (value !== null && isSeasonId(value));

const readStorage = (): SeasonPreference => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isSeasonPreference(stored) ? stored : 'auto';
  } catch {
    return 'auto';
  }
};

const writeStorage = (preference: SeasonPreference) => {
  try {
    if (preference === 'auto') {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, preference);
    }
  } catch {
    // Storage unavailable (private mode): the choice only lasts for this page.
  }
};

// `?season=<id|off|auto>` in the URL wins over the stored choice, e.g. to share a preview link.
const readUrl = (): SeasonPreference | null => {
  const param = new URLSearchParams(window.location.search).get(URL_PARAM);
  return isSeasonPreference(param) ? param : null;
};

let current: SeasonPreference = readUrl() ?? readStorage();
writeStorage(current);

export const getSeasonPreference = (): SeasonPreference => current;

export const setSeasonPreference = (preference: SeasonPreference) => {
  current = preference;
  writeStorage(preference);
  listeners.forEach((listener) => listener());
};

export const subscribeSeasonPreference = (listener: () => void) => {
  listeners.add(listener);

  // Keep the other tabs of the browser in sync.
  const onStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY || event.key === null) {
      current = readStorage();
      listener();
    }
  };
  window.addEventListener('storage', onStorage);

  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
};
